import { changeDataFormatToISO } from "common/helpers/changeDateFormatToISO";
import { newDate } from "common/helpers/newDate";
import { toISOString } from "common/helpers/toISOString";
import { BaseFilters } from "common/interfaces/common";
import i18n, { t } from "i18next";
import _ from "lodash";
import { store } from "store";
import { authAction } from "store/auth/authSlice";
import { noticeActions } from "store/notice/notice";

export class BaseApiService {
  constructor(uri: string) {
    this.url = `${process.env.REACT_APP_API}/${uri}/`;
  }

  protected url: string;

  protected get = <R = any>(uri: string, config?: RequestInit): Promise<R> => {
    return this.fetch(uri, this.config({ method: "GET", ...config }));
  };

  protected delete = <T extends BodyInit, R = any>(uri: string, body?: T, config?: RequestInit): Promise<R> =>
    this.fetch(uri, this.config({ method: "DELETE", body, ...config }));

  protected put = <T extends BodyInit, R = any>(uri: string, body: T, config?: RequestInit): Promise<R> =>
    this.fetch(uri, this.config({ method: "PUT", body, ...config }));

  protected post = <T extends BodyInit, R = any>(uri: string, body?: T, config?: RequestInit): Promise<R> =>
    this.fetch(uri, this.config({ method: "POST", body, ...config }));

  protected patch = <T extends BodyInit, R = any>(uri: string, body?: T, config?: RequestInit): Promise<R> =>
    this.fetch(uri, this.config({ method: "PATCH", body, ...config }));

  private config = (config: RequestInit) => {
    const token = store.getState().auth.authInfo.access_token;
    const headersForToken: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};

    return {
      ...config,
      headers: {
        ...headersForToken,
        ...(config.headers ? config.headers : { "Content-Type": "application/json" }),
      },
    };
  };

  private fetch = async (uri: string, config?: RequestInit) => {
    let response: Response;
    try {
      response = await fetch(`${this.url}${uri}`, config);
    } catch (e) {
      return await this.handleError({ status: 504, type: "errors.504" } as any);
    }

    return response.ok ? await this.onSuccess(response) : await this.handleError(response);
  };

  private onSuccess = (response: Response) => {
    if (response.status === 204 || response.headers.get("Content-Length") === "0") {
      return;
    }
    const typeResponse = response.headers.get("Content-Type");

    return typeResponse && /json/.test(typeResponse)
      ? response.json().catch(() => this.handleError(response))
      : response.blob().catch(() => this.handleError(response));
  };

  private handleError = async (response: Response) => {
    if (response.status === 401) {
      store.dispatch(authAction.clearAuthData());
      throw { isValidatorError: true, bodyError: { type: "tokenError" } };
    }
    if (response.status == 504) {
      store.dispatch(noticeActions.add({ type: "error", message: t(response.type) }));

      return;
    }

    const error = await response.json();
    console.log(error);

    if (response.status > 299 || response.status < 200) {
      if (error.Errors) {
        Object.keys(error.Errors).forEach((key) => {
          error.Errors[key]?.forEach((err: any) => {
            store.dispatch(
              noticeActions.add({
                type: "error",
                message: t(`back:${err.ErrorCode}`),
              })
            );
          });
        });
      } else if (error.type) {
        store.dispatch(noticeActions.add({ type: "error", message: t(`back:${error.type}`) }));
      }
    }

    throw { status: response.status, bodyError: error };
  };

  protected getODataQuery = <T extends BaseFilters>({
    pagination,
    sorting,
    pageSize = 10,
    withOutDeletedUtc,
    ...filters
  }: T) => {
    const locale = i18n.language === "ru" ? "Ru" : "En";
    const oDataQuery = ["$count=true&"];
    pageSize && oDataQuery.push(`$top=${pageSize}&`);

    pagination && oDataQuery.push(`$skip=${pagination * pageSize - pageSize}&`);

    oDataQuery.push(sorting ? `$orderby=${sorting}` : `$orderby=CreatedUtc desc`);
    !withOutDeletedUtc && oDataQuery.push("&$filter=DeletedUtc eq null");

    const filtersArray = Object.entries(filters).filter(([, value]) => !!value);
    filtersArray.length &&
      oDataQuery.push(
        (withOutDeletedUtc ? "&$filter=" : " and ") +
          filtersArray
            .map(([key, v]) => {
              const value = typeof v == "string" ? v.trim() : v;
              if (!value) {
                return "";
              }
              switch (key) {
                case "Name": {
                  return `contains(tolower(${key}), '${value.toLowerCase()}')`;
                }
                case "OppositeTeam":
                  return `MatchInfoStat/any(t: contains(tolower(t/Team/Name/${locale}), '${value.toLowerCase()}'))`;
                case "DisplayTeamInTheMedia":
                  return `${key} ${value === "true" ? "eq" : "ne"} true`;
                case "AnyInTeamArray":
                  return `Teams/any(Team: ${value.map((val: string) => `Team/Id eq ${val}`).join(" or ")})`;
                case "MediaHeader":
                  return `contains(tolower(MediaHeader/${locale}), '${value.toLowerCase()}')`;
                case "CategoryName":
                  return `contains(tolower(CategoryName/${locale}), '${value.toLowerCase()}')`;
                case "ComicSeasonName":
                case "ShortName":
                case "Amplua/Name":
                case "CategoryName":
                case "MediaHeader":
                case "FullName": {
                  return `contains(tolower(FullName/${locale}), '${value.toLowerCase()}')`;
                }
                case "Header":
                  return `contains(tolower(${key}/${locale}), '${value.toLowerCase()}')`;
                case "ComicName":
                  return `contains(tolower(Name/${locale}), '${value.toLowerCase()}')`;

                case "TeamsNames":
                  return `(contains(tolower(TeamHome${locale}Name), '${value.toLowerCase()}') or contains(tolower(TeamVisitor${locale}Name), '${value.toLowerCase()}'))`;
                case "Position":
                  return `contains(tolower(${key}), '${value.toLowerCase()}')`;

                case "StartDateSpecialOffer":
                case "EventDate":
                case "CreatedUtc":
                case "MatchDateTime":
                case "PublishDateTime":
                  const gt = toISOString(value.clone().set({ hour: 0, minute: 0, second: 0, millisecond: 0 }));
                  const lt = toISOString(
                    value.set({
                      hour: 23,
                      minute: 59,
                      second: 59,
                      millisecond: 0,
                    })
                  );

                  //Для фильтрации по StartDate в SpecialOffer от 00.00 до 23.59
                  return `${key === "StartDateSpecialOffer" ? "StartDate" : key} gt ${gt} and ${
                    key === "StartDateSpecialOffer" ? "StartDate" : key
                  } lt ${lt}`;

                case "MatchStartDateTime":
                  const from = toISOString(
                    value[0].set({
                      hour: 0,
                      minute: 0,
                      second: 0,
                      millisecond: 0,
                    })
                  );
                  const to = toISOString(
                    value[1].set({
                      hour: 23,
                      minute: 59,
                      second: 59,
                      millisecond: 0,
                    })
                  );

                  return `${key} gt ${from} and ${key} lt ${to}`;

                case "MatchDate": {
                  const valueNew = _.cloneDeep(value);
                  const gtt = toISOString(
                    valueNew.clone().set({
                      date: valueNew.date(),
                      hour: -4,
                      minute: 59,
                      second: 59,
                      millisecond: 0,
                    })
                  );
                  const ltt = toISOString(
                    valueNew.set({
                      date: valueNew.date(),
                      hour: 21,
                      minute: 0,
                      second: 0,
                      millisecond: 0,
                    })
                  );

                  return `MatchStartDateTime gt ${gtt} and MatchStartDateTime lt ${ltt}`;
                }

                // Даты, для которых имеет значение часовой пояс
                case "StartDateTime": {
                  const valueNew = _.cloneDeep(value);
                  const gtt = toISOString(
                    valueNew.clone().set({
                      date: valueNew.date(),
                      hour: -4,
                      minute: 59,
                      second: 59,
                      millisecond: 0,
                    })
                  );
                  const ltt = toISOString(
                    valueNew.set({
                      date: valueNew.date(),
                      hour: 21,
                      minute: 0,
                      second: 0,
                      millisecond: 0,
                    })
                  );

                  return `${key} gt ${gtt} and ${key} lt ${ltt}`;
                }

                case "MatchStartFromDate":
                  return `MatchStartDateTime gt ${toISOString(
                    value.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
                  )}`;

                case "StartDate":
                  const startDate = new Date(value);
                  startDate.setHours(0, 0, 0, 0);

                  return `${key} gt ${startDate.toISOString()}`;

                case "EndDate":
                  const endDate = new Date(value);

                  return `${key} lt ${endDate.toISOString()}`;

                case "StartVoting":
                  return `${key} gt ${newDate(value).toISOString()}`;

                case "EndVoting":
                  return `${key} lt ${newDate(value).toISOString()}`;

                case "Teams/Id":
                  return `Teams/any(Teams: Teams/Id eq ${value})`;

                case "Stadium/Id":
                  return `Stadium/Id eq ${value}`;

                case "OrganizationFilter": {
                  const normalizeYear = (year: string): string => {
                    if (year.length === 4) {
                      return year;
                    }

                    return `20${year.padStart(2, "0")}`;
                  };

                  const normalized = value.replace(/[\/,]/g, ".").trim();

                  const dateValue = normalized.match(/^(\d{2})\.(\d{2})(?:\.(\d{2}|\d{4}))?$/);
                  const isoDate = dateValue
                    ? dateValue[3]
                      ? `${normalizeYear(dateValue[3])}-${dateValue[2]}-${dateValue[1]}`
                      : `${dateValue[2]}-${dateValue[1]}`
                    : normalized.toLowerCase();

                  return `(contains(tolower(Name), '${normalized.toLowerCase()}') or contains(tolower(Ambassador/UserName/FirstName), '${normalized.toLowerCase()}') or contains(tolower(Ambassador/UserName/LastName), '${normalized.toLowerCase()}') or contains(tolower(cast(CreatedUtc, 'Edm.String')), '${isoDate}'))`;
                }
                case "SchoolTagId":
                  return `SchoolTagId eq ${value}`;

                case "SchoolTag/Id":
                  return `SchoolTag/Id eq ${value}`;

                case "AmbassadorFilter":
                  return `(contains(tolower(SchoolName), '${value.toLowerCase()}') or contains(tolower(UserName/FirstName), '${value.toLowerCase()}') or contains(tolower(UserName/LastName), '${value.toLowerCase()}'))`;

                case "MatchId":
                  return `MatchId eq ${value}`;

                // TO DO узнать почему не фильтрует по  or contains(tolower(AmbassadorName), '${value.toLowerCase()}')
                case "AmbassadorGatheringFilter":
                  return `(contains(tolower(SchoolName), '${value.toLowerCase()}'))`;

                case "GraduateSectionId":
                case "Tournament/Id":
                case "TournamentId":
                case "RoleId":
                  return `${key} eq ${value}`;

                case "SeasonId":
                  if (typeof value === "string") {
                    return `${key} eq ${value}`;
                  } else {
                    return `(${key} eq ${value[0]} or ${key} eq ${value[1]})`;
                  }

                case "SchoolId":
                  return `${key} eq ${value}`;

                case "MatchTeamId":
                  return `(${value
                    .map((val: string) => `TeamHomeId eq ${val} or TeamVisitorId eq ${val}`)
                    .join(" or ")})`;

                case "ActivationDate":
                  return `${key} ${value === "true" ? "ne" : "eq"} null`;

                case "IsDraft":
                  return `IsDraft ${value == "true" ? "eq" : "ne"} true`;

                case "SpartakKidsCardNumber":
                  return `startswith(cast(${key}, 'Edm.String'), '${value}')`;

                case "SortOrder":
                  return `${key} eq ${value}`;

                case "MediaStatus":
                  return `IsDraft ${value === "None" ? "eq" : "ne"} true and PublishDateTime ${
                    value === "Planned" ? "gt" : "le"
                  } ${new Date().toISOString()}`;

                case "Promo":
                  return value === "matchPlayer"
                    ? "Match ne null"
                    : value === "monthPlayer"
                    ? "Month ne null"
                    : "SeasonId ne null";
                case "IsAwaiting":
                  return `SentTime ${value === "await" ? "eq " : "ne "}${null}`;

                case "ParentChild":
                  return `(contains(tolower(ParentName), '${value.toLowerCase()}') or contains(tolower(ChildName), '${value.toLowerCase()}'))`;

                case "PhoneEmail":
                  return `startswith(Phone, '${value.replace("+", "")}') or contains(Email, '${value}')`;

                case "StartJournal":
                  return `CreatedUtc gt ${newDate(value).toISOString()}`;

                case "EndJournal":
                  return `CreatedUtc lt ${newDate(value).toISOString()}`;

                case "IdContains":
                  return `(contains(tolower(EntityName), '${value.toLowerCase()}') or contains(tolower(TableName), '${value.toLowerCase()}'))`;

                case "SectionByTeam":
                  return `Teams/any(Team: Team/Section eq '${value}')`;

                case "TrainerSection":
                  return `Teams/any(T: T/Team/Section  eq '${value}')`;

                case "TrainerTeamId":
                  return `Teams/any(T: T/Team/Id  eq ${value})`;

                case "NotNul":
                  return `${value} ne null`;

                case "CustomFilter":
                  return value;

                case "DateRangeStory":
                  return `(BeginningPublication ge ${changeDataFormatToISO(
                    value[0]
                  )} and EndPublication le ${changeDataFormatToISO(value[1])})`;

                case "Tournament":
                  return `Match/Tournament/Id eq ${value}`;

                case "ApplicationTournament":
                  return `contains(tolower(Tournament/Ru), '${value}')`;

                case "Participant":
                  return `Participants/any(p: contains(
                    tolower(concat(concat(p/FullName/FirstName, ' '), p/FullName/LastName)), 
                    '${value}'
                  ))`;

                case "SchoolName":
                  return `contains(tolower(Name), '${value}')`;

                case "Gender":
                  return `Match/MatchInfoStat/any(stat: stat/Team/GenderType eq '${value}')`;

                case "Id":
                  return `Id eq ${value}`;

                case `CoachTeam`:
                  return `Teams/any(stat: stat/Team/Id eq ${value})`;

                case `MedicalTeam`:
                  return `Teams/any(stat: stat/Id eq ${value})`;

                case "DateRange":
                  return `(StartPublish ge ${changeDataFormatToISO(value[0])} and EndPublish le ${changeDataFormatToISO(
                    value[1]
                  )})`;

                default:
                  return `${key} eq '${value}'`;
              }
            }, [])
            .join(" and ")
      );

    return oDataQuery.join("");
  };
}
